import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getMe } from "../features/authSlice";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import {
  FaArrowLeft,
  FaUpload,
  FaLeaf,
  FaBookOpen,
  FaFileAlt,
  FaImage,
  FaImages,
  FaTrash,
  FaTag,
  FaInstagram,
  FaChevronDown,
  FaNewspaper,
  FaPlus,
  FaTimes,
  FaCloudUploadAlt,
} from "react-icons/fa";

import { useStateContext } from "../contexts/ContextProvider";

// ── Quill toolbar config ─────────────────────────────────────────────────────
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    [{ align: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "blockquote", "code-block",
  "link", "image", "align",
];

const KATEGORI_OPTIONS = ["Case Study", "News", "Article", "Post"];

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ALBUM_PHOTOS = 50;

// ── Component ────────────────────────────────────────────────────────────────
const AddStories = () => {
  const [kategori, setKategori] = useState("");
  const [judul, setJudul] = useState("");
  const [tags, setTags] = useState("");
  const [deskripsi, setDeskripsi] = useState("");
  const [urlInstagram, setUrlInstagram] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── Album (multiple images) ──
  const [albumFiles, setAlbumFiles] = useState([]);
  const [albumPreviews, setAlbumPreviews] = useState([]);
  const [isDraggingAlbum, setIsDraggingAlbum] = useState(false);
  const [albumError, setAlbumError] = useState("");

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [kategoriOpen, setKategoriOpen] = useState(false);

  // ── Upload progress ──
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileInputRef = useRef(null);
  const albumInputRef = useRef(null);
  const kategoriRef = useRef(null);
  const { currentColor, currentMode } = useStateContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  const isPost = kategori === "Post";
  const isContent = ["Case Study", "News", "Article"].includes(kategori);

  // Apakah request ini membawa file (cover dan/atau album) sehingga progress bar relevan ditampilkan
  const hasFileUpload = !isPost && (!!file || albumFiles.length > 0);

  useEffect(() => { dispatch(getMe()); }, [dispatch]);
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) navigate("/");
  }, [navigate, user]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (kategoriRef.current && !kategoriRef.current.contains(e.target)) {
        setKategoriOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset fields when kategori changes
  const handleKategoriSelect = (val) => {
    setKategori(val);
    setKategoriOpen(false);
    setErrors({});
    if (val === "Post") {
      setJudul(""); setTags(""); setDeskripsi("");
      setFile(null); setPreview(null);
      setAlbumFiles([]); setAlbumPreviews([]); setAlbumError("");
    } else {
      setUrlInstagram("");
    }
  };

  // ── Cover file handlers ──────────────────────────────────────────────────
  const processFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };
  const handleFileChange = (e) => processFile(e.target.files[0]);
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); processFile(e.dataTransfer.files[0]); };
  const handleRemoveImage = (e) => { e?.stopPropagation(); setFile(null); setPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; };
  const handleUploadClick = (e) => { e?.stopPropagation(); fileInputRef.current?.click(); };

  // ── Album file handlers ──────────────────────────────────────────────────
  const processAlbumFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    setAlbumError("");

    const room = MAX_ALBUM_PHOTOS - albumFiles.length;
    if (room <= 0) {
      setAlbumError(`Maksimal ${MAX_ALBUM_PHOTOS} foto di dalam album.`);
      return;
    }

    const accepted = [];
    let rejectedReason = "";

    incoming.slice(0, room).forEach((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) {
        rejectedReason = "Beberapa file dilewati karena bukan format JPG/PNG.";
        return;
      }
      if (f.size > MAX_SIZE) {
        rejectedReason = "Beberapa file dilewati karena ukuran lebih dari 5MB.";
        return;
      }
      accepted.push(f);
    });

    if (incoming.length > room) {
      rejectedReason = `Hanya ${room} foto ditambahkan, sisanya melebihi batas ${MAX_ALBUM_PHOTOS} foto.`;
    }
    if (rejectedReason) setAlbumError(rejectedReason);

    if (accepted.length === 0) return;

    setAlbumFiles((prev) => [...prev, ...accepted]);

    accepted.forEach((f) => {
      const reader = new FileReader();
      reader.onloadend = () => setAlbumPreviews((prev) => [...prev, reader.result]);
      reader.readAsDataURL(f);
    });
  };

  const handleAlbumChange = (e) => {
    processAlbumFiles(e.target.files);
    if (albumInputRef.current) albumInputRef.current.value = "";
  };
  const handleAlbumDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAlbum(true); };
  const handleAlbumDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAlbum(false); };
  const handleAlbumDrop = (e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingAlbum(false); processAlbumFiles(e.dataTransfer.files); };
  const handleAlbumUploadClick = () => albumInputRef.current?.click();
  const handleRemoveAlbumImage = (index) => {
    setAlbumFiles((prev) => prev.filter((_, i) => i !== index));
    setAlbumPreviews((prev) => prev.filter((_, i) => i !== index));
    setAlbumError("");
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const saveStories = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("kategori", kategori);

    if (isPost) {
      formData.append("urlInstagram", urlInstagram.trim());
      formData.append("judul", "");
      formData.append("tags", "");
      formData.append("deskripsi", "");
    } else {
      formData.append("judul", judul.trim());
      formData.append("tags", tags.trim());
      formData.append("deskripsi", deskripsi);
      formData.append("urlInstagram", "");
      if (file) formData.append("file", file);
      albumFiles.forEach((albumFile) => formData.append("album", albumFile));
    }

    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.post(`${apiUrl}/stories`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });
      navigate("/list-stories");
    } catch (error) {
      alert(error.response?.data?.message || "Gagal menyimpan data stories");
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const inputStyle = (hasError) => ({
    backgroundColor: inputBg,
    borderColor: hasError ? "#ef4444" : inputBorder,
    color: textPrimary,
  });

  const KATEGORI_COLORS = {
    "Case Study": "#1a4731",
    "News": "#0f766e",
    "Article": "#7c3aed",
    "Post": "#be185d",
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background deco */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaLeaf className="absolute top-20 left-10 text-8xl" style={{ color: accentColor }} />
          <FaBookOpen className="absolute bottom-20 right-20 text-7xl" style={{ color: accentColor }} />
        </div>
      </div>

      {/* ── Overlay Loading / Progress saat submit ── */}
      {isSubmitting && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
        >
          <div
            className="w-full max-w-sm rounded-2xl border p-6 shadow-2xl"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                {hasFileUpload ? (
                  <FaCloudUploadAlt className="text-2xl animate-pulse" style={{ color: accentColor }} />
                ) : (
                  <span
                    className="block w-7 h-7 rounded-full border-4 animate-spin"
                    style={{ borderColor: `${accentColor}30`, borderTopColor: accentColor }}
                  />
                )}
              </div>

              <p className="text-sm font-semibold" style={{ color: textPrimary }}>
                {hasFileUpload
                  ? (uploadProgress < 100 ? "Mengunggah gambar..." : "Menyimpan data...")
                  : "Menyimpan Stories..."}
              </p>
              <p className="text-xs mt-1" style={{ color: textSecondary }}>
                {hasFileUpload
                  ? "Mohon tunggu, jangan tutup atau refresh halaman ini."
                  : "Mohon tunggu sebentar."}
              </p>

              {/* Progress bar hanya muncul kalau ada file/album yang diupload */}
              {hasFileUpload && (
                <div className="w-full mt-5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-medium" style={{ color: textSecondary }}>
                      {albumFiles.length > 0
                        ? `Mengunggah ${albumFiles.length} foto album${file ? " + cover" : ""}`
                        : "Mengunggah gambar cover"}
                    </span>
                    <span className="text-[11px] font-bold" style={{ color: accentColor }}>
                      {uploadProgress}%
                    </span>
                  </div>
                  <div
                    className="w-full h-2.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: isDark ? "#1a1a18" : "#f0ebe0" }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-200 ease-out"
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: accentColor,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 mx-auto">
        {/* ── Header ── */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/list-stories")}
            className="flex items-center gap-2 mb-4 transition-all duration-300 hover:opacity-80"
            style={{ color: textSecondary }}
          >
            <FaArrowLeft className="text-sm" />
            <span className="text-sm font-medium">Kembali ke Daftar Stories</span>
          </button>

          <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: accentColor }}>
                <FaBookOpen className="text-white text-xl" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                    Stories Management
                  </span>
                  <span className="text-xs" style={{ color: textSecondary }}>—</span>
                  <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                    Rumoh Pangan Aceh
                  </span>
                </div>
                <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>
                  Tambah Stories Baru
                </h1>
                <p className="text-sm mt-1" style={{ color: textSecondary }}>
                  Tambahkan artikel, berita, atau post baru untuk ditampilkan di website
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Form ── */}
        <form onSubmit={saveStories}>
          <div className="space-y-6">

            {/* ── Step 1: Pilih Kategori ── */}
            <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
              <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                <FaNewspaper className="text-sm" style={{ color: accentColor }} />
                Pilih Kategori
              </h3>

              {/* Custom combobox */}
              <div ref={kategoriRef} className="relative max-w-sm">
                <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                  Kategori <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setKategoriOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-300 outline-none text-sm"
                  style={{
                    backgroundColor: inputBg,
                    borderColor: errors.kategori ? "#ef4444" : kategoriOpen ? accentColor : inputBorder,
                    color: kategori ? textPrimary : textSecondary,
                  }}
                >
                  <div className="flex items-center gap-2">
                    {kategori && (
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: KATEGORI_COLORS[kategori] || accentColor }}
                      />
                    )}
                    <span>{kategori || "Pilih kategori..."}</span>
                  </div>
                  <FaChevronDown
                    className="text-xs transition-transform duration-200"
                    style={{
                      color: textSecondary,
                      transform: kategoriOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>

                {/* Dropdown */}
                {kategoriOpen && (
                  <div
                    className="absolute z-50 w-full mt-1 rounded-xl border shadow-xl overflow-hidden"
                    style={{ backgroundColor: cardBg, borderColor }}
                  >
                    {KATEGORI_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => handleKategoriSelect(opt)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-all duration-150"
                        style={{
                          backgroundColor: kategori === opt ? `${accentColor}15` : "transparent",
                          color: kategori === opt ? accentColor : textPrimary,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${accentColor}10`)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = kategori === opt ? `${accentColor}15` : "transparent")}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: KATEGORI_COLORS[opt] || accentColor }}
                        />
                        <span className="font-medium">{opt}</span>
                        {opt === "Post" && (
                          <span className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "#be185d20", color: "#be185d" }}>
                            <FaInstagram className="text-[9px]" /> Instagram
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {errors.kategori && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.kategori}</p>}
              </div>

              {/* Hint */}
              {kategori && (
                <p className="mt-3 text-xs" style={{ color: textSecondary }}>
                  {isPost
                    ? "Kategori Post hanya memerlukan URL embed Instagram."
                    : `Kategori ${kategori} memerlukan gambar, judul, tags, dan deskripsi artikel.`}
                </p>
              )}
            </div>

            {/* ── Conditional: Post ── */}
            {isPost && (
              <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
                <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                  <FaInstagram className="text-sm" style={{ color: "#be185d" }} />
                  Informasi Post Instagram
                </h3>

                <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                  URL Instagram <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FaInstagram className="absolute left-4 top-1/2 -translate-y-1/2 text-sm" style={{ color: "#be185d" }} />
                  <input
                    type="url"
                    value={urlInstagram}
                    onChange={(e) => setUrlInstagram(e.target.value)}
                    placeholder="https://www.instagram.com/p/..."
                    className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                    style={inputStyle(errors.urlInstagram)}
                    onFocus={(e) => (e.target.style.borderColor = "#be185d")}
                    onBlur={(e) => (e.target.style.borderColor = errors.urlInstagram ? "#ef4444" : inputBorder)}
                  />
                </div>
                {errors.urlInstagram && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.urlInstagram}</p>}
                <p className="text-xs mt-2" style={{ color: textSecondary }}>
                  Masukkan URL postingan Instagram yang ingin ditampilkan. Contoh: https://www.instagram.com/p/ABC123/
                </p>
              </div>
            )}

            {/* ── Conditional: Case Study / News / Article ── */}
            {isContent && (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Left – Image Upload (Cover + Album) */}
                <div className="lg:col-span-1">
                  <div className="p-6 rounded-xl border sticky top-6" style={{ backgroundColor: cardBg, borderColor }}>
                    <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: textPrimary }}>
                      <FaImage className="text-sm" style={{ color: accentColor }} />
                      Gambar Cover
                    </h3>
                    <div
                      className="relative rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden cursor-pointer"
                      style={{
                        borderColor: isDragging ? accentColor : inputBorder,
                        backgroundColor: isDragging ? `${accentColor}15` : inputBg,
                      }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={handleUploadClick}
                    >
                      {preview ? (
                        <div className="relative">
                          <img src={preview} alt="Preview" className="w-full h-56 object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                            <button type="button" onClick={handleUploadClick} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors">
                              <FaUpload className="text-white text-sm" />
                            </button>
                            <button type="button" onClick={handleRemoveImage} className="p-2 rounded-lg bg-red-500/80 hover:bg-red-500 transition-colors">
                              <FaTrash className="text-white text-sm" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${accentColor}20` }}>
                            <FaUpload className="text-2xl" style={{ color: accentColor }} />
                          </div>
                          <p className="text-sm font-medium text-center mb-1" style={{ color: textPrimary }}>
                            Drag & Drop gambar di sini
                          </p>
                          <p className="text-xs text-center" style={{ color: textSecondary }}>
                            atau klik untuk memilih file
                          </p>
                          <p className="text-[10px] mt-3 px-3 py-1 rounded-full" style={{ color: textSecondary, backgroundColor: `${accentColor}15` }}>
                            Format: JPG, PNG (Maks. 5MB)
                          </p>
                        </div>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                    </div>

                    {/* ── Album Foto (multiple) ── */}
                    <div className="mt-6 pt-6 border-t" style={{ borderColor }}>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: textPrimary }}>
                          <FaImages className="text-xs" style={{ color: accentColor }} />
                          Album Foto
                        </h4>
                        <span
                          className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                        >
                          Opsional
                        </span>
                      </div>
                      <p className="text-xs mb-4" style={{ color: textSecondary }}>
                        Tambahkan beberapa foto pendukung untuk galeri artikel ini.
                      </p>

                      <div className="grid grid-cols-3 gap-2">
                        {albumPreviews.map((src, idx) => (
                          <div
                            key={idx}
                            className="relative group rounded-lg overflow-hidden aspect-square border"
                            style={{ borderColor: inputBorder }}
                          >
                            <img src={src} alt={`Album ${idx + 1}`} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => handleRemoveAlbumImage(idx)}
                              className="absolute top-1 right-1 p-1.5 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            >
                              <FaTimes className="text-white text-[10px]" />
                            </button>
                          </div>
                        ))}

                        {albumFiles.length < MAX_ALBUM_PHOTOS && (
                          <div
                            onClick={handleAlbumUploadClick}
                            onDragOver={handleAlbumDragOver}
                            onDragLeave={handleAlbumDragLeave}
                            onDrop={handleAlbumDrop}
                            className="relative flex flex-col items-center justify-center aspect-square rounded-lg border-2 border-dashed cursor-pointer transition-all duration-300"
                            style={{
                              borderColor: isDraggingAlbum ? accentColor : inputBorder,
                              backgroundColor: isDraggingAlbum ? `${accentColor}15` : inputBg,
                            }}
                          >
                            <FaPlus className="text-sm mb-1" style={{ color: textSecondary }} />
                            <span className="text-[10px] font-medium text-center px-1" style={{ color: textSecondary }}>
                              Tambah
                            </span>
                          </div>
                        )}
                      </div>

                      <input
                        ref={albumInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAlbumChange}
                        className="hidden"
                      />

                      {albumError && (
                        <p className="text-xs mt-3" style={{ color: "#ef4444" }}>{albumError}</p>
                      )}

                      <div className="flex items-center justify-between mt-3">
                        <p className="text-[11px]" style={{ color: textSecondary }}>
                          {albumFiles.length > 0
                            ? `${albumFiles.length}/${MAX_ALBUM_PHOTOS} foto dipilih`
                            : "Belum ada foto album"}
                        </p>
                        <p className="text-[11px]" style={{ color: textSecondary }}>
                          Maks. 10MB / foto
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right – Fields */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Basic Info */}
                  <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
                    <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                      <FaFileAlt className="text-sm" style={{ color: accentColor }} />
                      Informasi Artikel
                    </h3>

                    {/* Judul */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                        Judul <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FaFileAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-sm" style={{ color: textSecondary }} />
                        <input
                          type="text"
                          value={judul}
                          onChange={(e) => setJudul(e.target.value)}
                          placeholder="Masukkan judul artikel"
                          className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                          style={inputStyle(errors.judul)}
                          onFocus={(e) => (e.target.style.borderColor = accentColor)}
                          onBlur={(e) => (e.target.style.borderColor = errors.judul ? "#ef4444" : inputBorder)}
                        />
                      </div>
                      {errors.judul && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.judul}</p>}
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
                        Tags
                      </label>
                      <div className="relative">
                        <FaTag className="absolute left-4 top-1/2 -translate-y-1/2 text-sm" style={{ color: textSecondary }} />
                        <input
                          type="text"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="Contoh: Ketahanan Pangan, Aceh, Pertanian"
                          className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                          style={inputStyle(false)}
                          onFocus={(e) => (e.target.style.borderColor = accentColor)}
                          onBlur={(e) => (e.target.style.borderColor = inputBorder)}
                        />
                      </div>
                      <p className="text-xs mt-1.5" style={{ color: textSecondary }}>
                        Pisahkan setiap tag dengan koma (,)
                      </p>
                    </div>
                  </div>

                  {/* Quill Editor */}
                  <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
                    <h3 className="font-semibold mb-5 flex items-center gap-2" style={{ color: textPrimary }}>
                      <FaFileAlt className="text-sm" style={{ color: accentColor }} />
                      Deskripsi / Konten Artikel <span className="text-red-500">*</span>
                    </h3>

                    <div
                      className="rounded-lg overflow-hidden"
                      style={{ border: `1px solid ${errors.deskripsi ? "#ef4444" : inputBorder}` }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={deskripsi}
                        onChange={setDeskripsi}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Tulis konten artikel di sini..."
                      />
                    </div>
                    {errors.deskripsi && <p className="text-xs mt-1" style={{ color: "#ef4444" }}>{errors.deskripsi}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* ── Action Buttons ── */}
            {kategori && (
              <div
                className="p-6 rounded-xl border flex flex-col sm:flex-row gap-4 justify-end"
                style={{ backgroundColor: cardBg, borderColor }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/list-stories")}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: isDark ? "#1a1a18" : "#f7f3ec", color: textPrimary, border: `1px solid ${borderColor}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isDark ? "#282C33" : "#e8e0d0")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isDark ? "#1a1a18" : "#f7f3ec")}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accentColor }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#b5772a")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accentColor)}
                >
                  {isSubmitting ? (
                    <span
                      className="block w-4 h-4 rounded-full border-2 animate-spin"
                      style={{ borderColor: "rgba(255,255,255,0.4)", borderTopColor: "#fff" }}
                    />
                  ) : (
                    <FaLeaf className="text-sm" />
                  )}
                  {isSubmitting
                    ? (hasFileUpload ? `Mengunggah... ${uploadProgress}%` : "Menyimpan...")
                    : "Simpan Stories"}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* ── Quill + Dark mode overrides ── */}
      <style>{`
        /* Toolbar */
        .ql-toolbar.ql-snow {
          border: none !important;
          border-bottom: 1px solid ${borderColor} !important;
          background-color: ${isDark ? "#1a1a18" : "#f7f3ec"} !important;
          padding: 10px 12px !important;
          border-radius: 0 !important;
        }
        .ql-toolbar.ql-snow .ql-stroke { stroke: ${textSecondary} !important; }
        .ql-toolbar.ql-snow .ql-fill { fill: ${textSecondary} !important; }
        .ql-toolbar.ql-snow button:hover .ql-stroke,
        .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: ${accentColor} !important; }
        .ql-toolbar.ql-snow button:hover .ql-fill,
        .ql-toolbar.ql-snow button.ql-active .ql-fill { fill: ${accentColor} !important; }
        .ql-toolbar.ql-snow .ql-picker-label { color: ${textSecondary} !important; }
        .ql-toolbar.ql-snow .ql-picker-label:hover { color: ${accentColor} !important; }
        .ql-toolbar.ql-snow .ql-picker-options {
          background: ${cardBg} !important;
          border-color: ${borderColor} !important;
          border-radius: 8px !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
        }
        .ql-toolbar.ql-snow .ql-picker-item { color: ${textPrimary} !important; }
        .ql-toolbar.ql-snow .ql-picker-item:hover { color: ${accentColor} !important; }

        /* Editor container */
        .ql-container.ql-snow {
          border: none !important;
          font-size: 14px !important;
          font-family: inherit !important;
        }
        .ql-editor {
          min-height: 280px !important;
          background-color: ${inputBg} !important;
          color: ${textPrimary} !important;
          line-height: 1.85 !important;
          padding: 16px 18px !important;
        }
        .ql-editor.ql-blank::before {
          color: ${textSecondary} !important;
          font-style: normal !important;
        }

        /* Content styles (matching ViewStories) */
        .ql-editor p { margin-bottom: 1rem; color: ${isDark ? "#c8c4bc" : "#4a4a44"}; }
        .ql-editor h1 {
          font-family: Georgia, serif;
          font-size: 1.75rem; font-weight: 700;
          color: ${textPrimary}; margin: 2rem 0 0.75rem; line-height: 1.2;
        }
        .ql-editor h2 {
          font-family: Georgia, serif;
          font-size: 1.35rem; font-weight: 700;
          color: ${textPrimary}; margin: 2rem 0 0.75rem;
          padding-bottom: 0.4rem;
          border-bottom: 2px solid ${borderColor};
        }
        .ql-editor h3 {
          font-family: Georgia, serif;
          font-size: 1.1rem; font-weight: 700;
          color: ${textPrimary}; margin: 1.5rem 0 0.5rem;
        }
        .ql-editor strong { font-weight: 700; color: ${textPrimary}; }
        .ql-editor em { font-style: italic; }
        .ql-editor ul, .ql-editor ol { margin: 0.75rem 0 1.25rem; padding-left: 0; list-style: none; }
        .ql-editor ul > li {
          position: relative; padding: 0.5rem 0.75rem 0.5rem 2.25rem;
          margin-bottom: 0.35rem;
          background: ${isDark ? "#1a1a1808" : "#f7f3ec"};
          border-left: 3px solid ${accentColor};
          color: ${isDark ? "#c8c4bc" : "#3a3a34"};
          font-size: 0.875rem; line-height: 1.7;
        }
        .ql-editor ul > li::before {
          content: '✳'; position: absolute;
          left: 0.6rem; top: 0.5rem;
          color: ${accentColor}; font-size: 0.7rem; font-weight: 700;
        }
        .ql-editor blockquote {
          border-left: 4px solid ${accentColor} !important;
          background: ${isDark ? "#1a1a18" : "#f7f3ec"} !important;
          padding: 1rem 1.25rem !important; margin: 1.5rem 0 !important;
          font-family: Georgia, serif !important;
          font-size: 1.05rem !important; font-style: italic !important;
          color: ${textPrimary} !important;
        }
        .ql-editor a { color: ${accentColor}; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default AddStories;